import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import type { ZodType } from 'zod';
import type { LLMGenerateOptions } from '@/IsekaiRPG/modules/llm/llmTypes';
import { LLM_DEBUG_CONFIG } from '@/IsekaiRPG/modules/llm/llmDebugConfig';
import { LLMEmptyResponseError, LLMInteractionError, LLMUpstreamError } from '@/IsekaiRPG/modules/llm/llmErrors';
import { PromptBuilder } from '@/IsekaiRPG/modules/llm/llmPromptBuilder';
import { ResponseParser } from '@/IsekaiRPG/modules/llm/llmResponseParser';
import { LLMProvider } from './llmProviderRuntime';

export interface LLMGenerateAndParseOptions {
  /** 是否打印该次调用的原始输出；未传则走全局开关 */
  rawOutputLogEnabled?: boolean;
  /** 日志前缀标签（便于区分调用来源） */
  rawOutputLogLabel?: string;
  /** 可选的请求中断信号，用于取消进行中的 LLM 请求 */
  signal?: AbortSignal;
}

export interface LLMLastInteractionSnapshot {
  timestamp: number;
  requestId: string;
  mode: 'generate' | 'generateAndParse' | 'generateAndParseMulti';
  outputTag?: string;
  outputTags?: string[];
  messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
  response: string;
}

export class LLMClient {
  private static TAG = 'LLMClient';
  private static lastInteractionSnapshot: LLMLastInteractionSnapshot | null = null;

  public static getLastInteractionSnapshot(): LLMLastInteractionSnapshot | null {
    return this.lastInteractionSnapshot
      ? { ...this.lastInteractionSnapshot, messages: [...this.lastInteractionSnapshot.messages] }
      : null;
  }

  public static async generate(
    systemInstruction: string,
    userInstruction: string,
    options?: LLMGenerateAndParseOptions,
  ): Promise<string> {
    const requestId = this.createRequestId();
    const messages = [
      ...(systemInstruction.trim().length > 0 ? [{ role: 'system' as const, content: systemInstruction.trim() }] : []),
      { role: 'user' as const, content: userInstruction.trim() },
    ];

    try {
      LogService.info(this.TAG, `[${requestId}] 正在调用 LLM...`);

      const response = await LLMProvider.generate({
        request_id: requestId,
        messages,
        generation_type: 'normal',
        should_stream: false,
        signal: options?.signal,
      });

      if (!response) {
        throw new LLMEmptyResponseError('LLM 返回为空', { source: 'LLMClient.generate' });
      }

      this.lastInteractionSnapshot = {
        timestamp: Date.now(),
        requestId,
        mode: 'generate',
        messages: messages.map(message => ({ role: message.role, content: message.content })),
        response,
      };

      const shouldLogRaw = options?.rawOutputLogEnabled ?? LLM_DEBUG_CONFIG.LLM_CLIENT_RAW_OUTPUT_LOG_ENABLED;
      if (shouldLogRaw) {
        const logLabel = options?.rawOutputLogLabel || 'LLM 原始回复';
        LogService.info(this.TAG, `[${requestId}] ${logLabel}:`, response);
      }

      return response;
    } catch (error) {
      LogService.error(this.TAG, `[${requestId}] LLM 生成失败`, error);
      if (error instanceof LLMInteractionError) {
        throw error;
      }
      throw new LLMUpstreamError('LLM 生成失败', { source: 'LLMClient.generate', requestId }, error);
    }
  }

  /**
   * 通用生成方法 (集成 PromptBuilder 和 ResponseParser)
   */
  public static async generateAndParse<T>(
    systemInstruction: string,
    userInstruction: string,
    outputTag: string,
    schema: ZodType<T>,
    schemaDescription: string = '',
    options?: LLMGenerateAndParseOptions,
  ): Promise<T> {
    const requestId = this.createRequestId();
    const messages = PromptBuilder.buildJsonMessages(systemInstruction, userInstruction, outputTag, schemaDescription);

    try {
      LogService.info(this.TAG, `[${requestId}] 正在调用 LLM...`);

      const response = await LLMProvider.generate({
        request_id: requestId,
        messages,
        generation_type: 'normal',
        should_stream: false,
        signal: options?.signal,
      });

      if (!response) {
        throw new LLMEmptyResponseError('LLM 返回为空', {
          source: 'LLMClient.generateAndParse',
          outputTag,
          requestId,
        });
      }

      this.lastInteractionSnapshot = {
        timestamp: Date.now(),
        requestId,
        mode: 'generateAndParse',
        outputTag,
        messages: messages.map(message => ({ role: message.role, content: message.content })),
        response,
      };

      const shouldLogRaw = options?.rawOutputLogEnabled ?? LLM_DEBUG_CONFIG.LLM_CLIENT_RAW_OUTPUT_LOG_ENABLED;
      if (shouldLogRaw) {
        const logLabel = options?.rawOutputLogLabel || 'LLM 原始回复';
        LogService.info(this.TAG, `[${requestId}] ${logLabel}:`, response);
      }

      // 解析 XML 并校验 JSON
      const parseStartedAt = Date.now();
      const parsed = ResponseParser.parseFromXml<T>(response, outputTag, schema, { requestId });
      LogService.info(this.TAG, `[${requestId}] 响应解析完成`, {
        outputTag,
        parseMs: Date.now() - parseStartedAt,
      });
      return parsed;
    } catch (error) {
      LogService.error(this.TAG, `[${requestId}] LLM 生成或解析失败`, error);
      if (error instanceof LLMInteractionError) {
        throw error;
      }
      throw new LLMUpstreamError(
        'LLM 生成或解析失败',
        { source: 'LLMClient.generateAndParse', outputTag, requestId },
        error,
      );
    }
  }

  /**
   * 基于完整 LLMGenerateOptions 调用并解析结构化输出
   * 适用于需要复用花园消息流、世界书与历史上下文的链路。
   */
  public static async generateWithOptionsAndParse<T>(
    generateOptions: LLMGenerateOptions,
    outputTag: string,
    schema: ZodType<T>,
    schemaDescription: string = '',
    options?: LLMGenerateAndParseOptions,
  ): Promise<T> {
    const requestId = generateOptions.request_id?.trim() || this.createRequestId();
    const messages = [
      ...(generateOptions.messages ?? []),
    ];

    if (messages.length === 0) {
      throw new LLMUpstreamError('LLM 生成或解析失败', {
        source: 'LLMClient.generateWithOptionsAndParse',
        outputTag,
        requestId,
      });
    }

    const nextMessages = [...messages];
    const lastMessage = nextMessages[nextMessages.length - 1];
    const userPrompt = PromptBuilder.buildJsonUserPrompt(lastMessage?.content || '', outputTag, schemaDescription);

    if (!lastMessage || lastMessage.role !== 'user') {
      nextMessages.push({
        role: 'user',
        content: userPrompt,
      });
    } else {
      nextMessages[nextMessages.length - 1] = {
        role: 'user',
        content: userPrompt,
      };
    }

    try {
      LogService.info(this.TAG, `[${requestId}] 正在调用 LLM...`);

      const response = await LLMProvider.generate({
        ...generateOptions,
        request_id: requestId,
        messages: nextMessages,
        generation_type: generateOptions.generation_type ?? 'normal',
        should_stream: false,
        signal: options?.signal ?? generateOptions.signal,
      });

      if (!response) {
        throw new LLMEmptyResponseError('LLM 返回为空', {
          source: 'LLMClient.generateWithOptionsAndParse',
          outputTag,
          requestId,
        });
      }

      this.lastInteractionSnapshot = {
        timestamp: Date.now(),
        requestId,
        mode: 'generateAndParse',
        outputTag,
        messages: nextMessages.map(message => ({ role: message.role, content: message.content })),
        response,
      };

      const shouldLogRaw = options?.rawOutputLogEnabled ?? LLM_DEBUG_CONFIG.LLM_CLIENT_RAW_OUTPUT_LOG_ENABLED;
      if (shouldLogRaw) {
        const logLabel = options?.rawOutputLogLabel || 'LLM 原始回复';
        LogService.info(this.TAG, `[${requestId}] ${logLabel}:`, response);
      }

      const parseStartedAt = Date.now();
      const parsed = ResponseParser.parseFromXml<T>(response, outputTag, schema, { requestId });
      LogService.info(this.TAG, `[${requestId}] 响应解析完成`, {
        outputTag,
        parseMs: Date.now() - parseStartedAt,
      });
      return parsed;
    } catch (error) {
      LogService.error(this.TAG, `[${requestId}] LLM 生成或解析失败`, error);
      if (error instanceof LLMInteractionError) {
        throw error;
      }
      throw new LLMUpstreamError(
        'LLM 生成或解析失败',
        { source: 'LLMClient.generateWithOptionsAndParse', outputTag, requestId },
        error,
      );
    }
  }

  /**
   * 通用多标签解析方法（单次调用，按标签分别解析）
   */
  public static async generateAndParseMulti<T extends Record<string, unknown>>(
    systemInstruction: string,
    userInstruction: string,
    parseMap: { [K in keyof T]: { outputTag: string; schema: ZodType<T[K]> } },
    options?: LLMGenerateAndParseOptions,
  ): Promise<T> {
    const requestId = this.createRequestId();
    try {
      const messages = [
        ...(systemInstruction.trim().length > 0
          ? [{ role: 'system' as const, content: systemInstruction.trim() }]
          : []),
        { role: 'user' as const, content: userInstruction.trim() },
      ];
      const response = await LLMProvider.generate({
        request_id: requestId,
        messages,
        generation_type: 'normal',
        should_stream: false,
        signal: options?.signal,
      });

      if (!response) {
        throw new LLMEmptyResponseError('LLM 返回为空', {
          source: 'LLMClient.generateAndParseMulti',
          requestId,
        });
      }

      this.lastInteractionSnapshot = {
        timestamp: Date.now(),
        requestId,
        mode: 'generateAndParseMulti',
        outputTags: (Object.values(parseMap) as Array<{ outputTag: string }>).map(item => item.outputTag),
        messages: messages.map(message => ({ role: message.role, content: message.content })),
        response,
      };

      const shouldLogRaw = options?.rawOutputLogEnabled ?? LLM_DEBUG_CONFIG.LLM_CLIENT_RAW_OUTPUT_LOG_ENABLED;
      if (shouldLogRaw) {
        const logLabel = options?.rawOutputLogLabel || 'LLM 原始回复';
        LogService.info(this.TAG, `[${requestId}] ${logLabel}:`, response);
      }

      const result = {} as T;

      for (const key of Object.keys(parseMap) as Array<keyof T>) {
        const { outputTag, schema } = parseMap[key];
        const parseStartedAt = Date.now();
        result[key] = ResponseParser.parseFromXml<T[typeof key]>(response, outputTag, schema, { requestId });
        LogService.info(this.TAG, `[${requestId}] 响应解析完成`, {
          outputTag,
          parseMs: Date.now() - parseStartedAt,
        });
      }

      return result;
    } catch (error) {
      LogService.error(this.TAG, `[${requestId}] LLM 多标签生成或解析失败`, error);
      if (error instanceof LLMInteractionError) {
        throw error;
      }
      throw new LLMUpstreamError(
        'LLM 多标签生成或解析失败',
        { source: 'LLMClient.generateAndParseMulti', requestId },
        error,
      );
    }
  }

  /**
   * 基于完整 LLMGenerateOptions 调用并解析多个结构化输出
   * 适用于需要复用花园消息流、世界书与历史上下文的多标签链路。
   */
  public static async generateWithOptionsAndParseMulti<T extends Record<string, unknown>>(
    generateOptions: LLMGenerateOptions,
    parseMap: { [K in keyof T]: { outputTag: string; schema: ZodType<T[K]> } },
    options?: LLMGenerateAndParseOptions,
  ): Promise<T> {
    const requestId = generateOptions.request_id?.trim() || this.createRequestId();
    const messages = [...(generateOptions.messages ?? [])];

    if (messages.length === 0) {
      throw new LLMUpstreamError('LLM 多标签生成或解析失败', {
        source: 'LLMClient.generateWithOptionsAndParseMulti',
        requestId,
      });
    }

    try {
      LogService.info(this.TAG, `[${requestId}] 正在调用 LLM...`);

      const response = await LLMProvider.generate({
        ...generateOptions,
        request_id: requestId,
        messages,
        generation_type: generateOptions.generation_type ?? 'normal',
        should_stream: false,
        signal: options?.signal ?? generateOptions.signal,
      });

      if (!response) {
        throw new LLMEmptyResponseError('LLM 返回为空', {
          source: 'LLMClient.generateWithOptionsAndParseMulti',
          requestId,
        });
      }

      this.lastInteractionSnapshot = {
        timestamp: Date.now(),
        requestId,
        mode: 'generateAndParseMulti',
        outputTags: (Object.values(parseMap) as Array<{ outputTag: string }>).map(item => item.outputTag),
        messages: messages.map(message => ({ role: message.role, content: message.content })),
        response,
      };

      const shouldLogRaw = options?.rawOutputLogEnabled ?? LLM_DEBUG_CONFIG.LLM_CLIENT_RAW_OUTPUT_LOG_ENABLED;
      if (shouldLogRaw) {
        const logLabel = options?.rawOutputLogLabel || 'LLM 原始回复';
        LogService.info(this.TAG, `[${requestId}] ${logLabel}:`, response);
      }

      const result = {} as T;
      for (const key of Object.keys(parseMap) as Array<keyof T>) {
        const { outputTag, schema } = parseMap[key];
        const parseStartedAt = Date.now();
        result[key] = ResponseParser.parseFromXml<T[typeof key]>(response, outputTag, schema, { requestId });
        LogService.info(this.TAG, `[${requestId}] 响应解析完成`, {
          outputTag,
          parseMs: Date.now() - parseStartedAt,
        });
      }

      return result;
    } catch (error) {
      LogService.error(this.TAG, `[${requestId}] LLM 多标签生成或解析失败`, error);
      if (error instanceof LLMInteractionError) {
        throw error;
      }
      throw new LLMUpstreamError(
        'LLM 多标签生成或解析失败',
        { source: 'LLMClient.generateWithOptionsAndParseMulti', requestId },
        error,
      );
    }
  }

  private static createRequestId(): string {
    return `llmcli_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }
}
